import React, { FC, useEffect } from 'react';

import { Box, Typography } from '@mui/material';
import { useFormik } from 'formik';

import { adminOwnersList, ConfirmationTypes, getOwnersList } from '@pharmaplan/common';
import { dropdownMapper } from '@pharmaplan/common/helpers/utils';

import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { resetDialog } from '../../../../reducers/dialogReducer';
import { TextFieldVariants } from '../../../../helpers/Constants';
import ConfirmationModal from '../../../Modals/ConfirmationModal';
import CustomDropdown from '../../../common/CustomDropdown';
import strings from '../../../../localization';

import validator from './validator';
import styles from './styles';

interface IOwnershipModal {
  /** Accounts the picked owner is assigned to. A single id for the row action. */
  pharmacyIds: Array<string>;
  currentOwnerId?: null | string;
  onConfirm: (ownerId: string) => void;
}

/**
 * Assign / change ownership. Both share the generic confirmation modal and only
 * differ by the wording, so the owner dropdown lives inside its message slot.
 */
const OwnershipModal: FC<IOwnershipModal> = ({
  pharmacyIds,
  currentOwnerId,
  onConfirm,
}) => {
  const dispatch = useAppDispatch();

  const owners = useAppSelector(adminOwnersList);

  const isChange = !!currentOwnerId;
  const { length } = pharmacyIds;

  const message = isChange
    ? strings.areYouSureChangeOwnership
    : strings.areYouSureAssignOwnership;

  const formik = useFormik({
    initialValues: { ownerId: currentOwnerId ?? '' },
    validationSchema: validator,
    onSubmit: ({ ownerId }) => {
      onConfirm(ownerId);
      dispatch(resetDialog());
    },
  });

  useEffect(() => {
    dispatch(getOwnersList());
  }, [dispatch]);

  return (
    <ConfirmationModal
      type={isChange
        ? ConfirmationTypes.changeOwnership
        : ConfirmationTypes.assignOwnership}
      confirmAction={() =>
        formik.handleSubmit()}
      customMessage={(
        <Box sx={styles.container}>
          <Typography sx={styles.message}>
            {length > 1
              ? strings.formatString(
                strings.assignOwnerToAccountsFormat,
                `${length}`,
              )
              : message}
          </Typography>
          <CustomDropdown
            formik={formik}
            name="ownerId"
            id="ownerId"
            label={strings.owner}
            placeholder={strings.selectAnAdmin}
            variant={TextFieldVariants.filled}
            menuItems={dropdownMapper(owners, 'userId', 'userName')}
          />
        </Box>
      )}
    />
  );
};

export default OwnershipModal;
